"use client";

import { cn, getInitials } from "@istari-frontend/shared";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { Avatar as AvatarPrimitive } from "radix-ui";
import * as React from "react";
import { useMemo } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const avatarRootVariants = cva("relative flex shrink-0 overflow-hidden", {
  variants: {
    size: {
      "3xs": "size-4",
      "2xs": "size-5",
      xs: "size-6",
      sm: "size-8",
      md: "size-10",
      lg: "size-12",
      xl: "size-16",
    },
    shape: {
      circle: "rounded-full",
      rounded: "rounded-md",
      square: "rounded-none",
    },
    ring: {
      true: "ring-2 ring-background",
      false: "",
    },
  },
  defaultVariants: {
    size: "sm",
    shape: "circle",
    ring: false,
  },
});

type AvatarRootProps = React.ComponentProps<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarRootVariants>;

export function Avatar({ className, size, shape, ring, ...props }: AvatarRootProps) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarRootVariants({ size, shape, ring }), className)}
      {...props}
    />
  );
}

type AvatarImageProps = React.ComponentProps<typeof AvatarPrimitive.Image> & { grayscale?: boolean };

export function AvatarImage({ className, grayscale, ...props }: AvatarImageProps) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full", grayscale ? "grayscale" : "", className)}
      {...props}
    />
  );
}

const avatarFallbackVariants = cva("flex size-full items-center justify-center cursor-default", {
  variants: {
    size: {
      "3xs": "text-[0.4375rem]",
      "2xs": "text-[0.5rem]",
      xs: "text-[0.625rem]",
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
      xl: "text-lg",
    },
    tone: {
      muted: "bg-muted text-muted-foreground",
      primary: "bg-primary text-primary-foreground",
      secondary: "bg-secondary text-secondary-foreground",
      accent: "bg-accent text-accent-foreground",
      destructive: "bg-destructive text-destructive-foreground",
    },
    shape: {
      circle: "rounded-full",
      rounded: "rounded-md",
      square: "rounded-none",
    },
  },
  defaultVariants: {
    tone: "muted",
    shape: "circle",
  },
});

type AvatarFallbackProps = React.ComponentProps<typeof AvatarPrimitive.Fallback> &
  VariantProps<typeof avatarFallbackVariants>;

export function AvatarFallback({ className, tone, shape, size, ...props }: AvatarFallbackProps) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(avatarFallbackVariants({ tone, shape, size }), className)}
      {...props}
    />
  );
}

// --- AvatarList (composable) ---
type AvatarSize = "3xs" | "2xs" | "xs" | "sm" | "md" | "lg" | "xl";
type AvatarShape = "circle" | "rounded" | "square";
type AvatarTone = "muted" | "primary" | "secondary" | "accent" | "destructive";

export type AvatarListUser = {
  id?: string | number | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  email?: string | null;
  imageUrl?: string | null;
};

type AvatarListProps = {
  users?: AvatarListUser[];
  max?: number;
  size?: AvatarSize;
  shape?: AvatarShape;
  ring?: boolean;
  grayscale?: boolean;
  overlap?: boolean;
  fallbackTone?: AvatarTone;
  className?: string;
  getImageSrc?: (user: AvatarListUser) => string | undefined | null;
  getInitials?: (user: AvatarListUser) => string;
  loading?: boolean;
  loadingPosition?: "left" | "right";
  showTooltip?: boolean;
  stackDirection?: "earlier-on-top" | "later-on-top";
  ringClassName?: string;
  avatarClassName?: string;
  overflowBubbleClassName?: string;
};

const sizeToBubble: Record<AvatarSize, string> = {
  "3xs": "size-4 text-[0.4375rem]",
  "2xs": "size-5 text-[0.5rem]",
  xs: "size-6 text-[0.625rem]",
  sm: "size-8 text-[0.625rem]",
  md: "size-10 text-[0.6875rem]",
  lg: "size-12 text-xs",
  xl: "size-16 text-sm",
};

const sizeToOverlap: Record<AvatarSize, string> = {
  "3xs": "-space-x-1",
  "2xs": "-space-x-0.5",
  xs: "-space-x-1",
  sm: "-space-x-2",
  md: "-space-x-2.5",
  lg: "-space-x-3",
  xl: "-space-x-4",
};

const sizeToGap: Record<AvatarSize, string> = {
  "3xs": "gap-0.5",
  "2xs": "gap-0.5",
  xs: "gap-1",
  sm: "gap-1.5",
  md: "gap-2",
  lg: "gap-2.5",
  xl: "gap-3",
};

const defaultInitials = (u: AvatarListUser) =>
  getInitials(u.fullName) ||
  getInitials({ first: u.firstName, last: u.lastName }) ||
  u.email?.[0]?.toUpperCase() ||
  "?";

const defaultGetImageSrc = (u: AvatarListUser) => u.imageUrl ?? undefined;

export function AvatarList({
  users = [],
  max = 6,
  size = "sm",
  shape = "circle",
  ring = true,
  grayscale = false,
  overlap = true,
  fallbackTone = "muted",
  className,
  getImageSrc = defaultGetImageSrc,
  getInitials = defaultInitials,
  loading = false,
  loadingPosition = "right",
  showTooltip = true,
  stackDirection = "earlier-on-top",
  ringClassName,
  avatarClassName,
  overflowBubbleClassName,
}: AvatarListProps) {
  const visible = useMemo(() => users.slice(0, Math.max(0, max)), [users, max]);
  const overflow = Math.max(0, users.length - visible.length);

  const title = useMemo(() => {
    return users
      .map((u) => u.fullName || `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email || "User")
      .join(", ");
  }, [users]);

  const containerSpacing = overlap ? sizeToOverlap[size] : sizeToGap[size];

  if (loading) {
    /** @todo add loading animation
     * loading text should fade out with a blur
     * the loading avatar should "unfold" in to all of the avatars in the list by translating x in the correct direction based on the right or left prop
     */

    return (
      <div className={cn("flex items-center gap-2", className)}>
        {loadingPosition === "left" && <span className="text-muted-foreground text-xs">Loading...</span>}
        <Avatar size={size} shape={shape} ring={ring}>
          <AvatarFallback tone={fallbackTone} shape={shape} size={size}>
            <Loader2 className="animate-spin" size={sizeToLoaderSize[size]} />
          </AvatarFallback>
        </Avatar>
        {loadingPosition === "right" && <span className="text-muted-foreground text-xs">Loading...</span>}
      </div>
    );
  }

  const content = (
    <div className={cn("flex items-center", containerSpacing, className)} aria-label={title}>
      {visible.map((u, idx) => {
        const key = (u.id ?? `${u.email ?? u.fullName ?? idx}`) as React.Key;
        const src = getImageSrc(u) ?? undefined;
        const initials = getInitials(u);
        const zIndex = stackDirection === "later-on-top" ? idx + 1 : visible.length - idx;
        return (
          <Avatar
            key={key}
            size={size}
            shape={shape}
            ring={ring}
            style={{ zIndex }}
            className={cn(ringClassName, avatarClassName)}
          >
            <AvatarImage src={src ?? undefined} alt={u.fullName ?? initials} grayscale={grayscale} />
            <AvatarFallback tone={fallbackTone} shape={shape} size={size}>
              {initials}
            </AvatarFallback>
          </Avatar>
        );
      })}
      {overflow > 0 ? (
        <div
          className={cn(
            "bg-muted text-muted-foreground flex items-center justify-center rounded-full",
            sizeToBubble[size],
            ringClassName,
            overflowBubbleClassName,
          )}
          style={{ zIndex: stackDirection === "later-on-top" ? visible.length + 1 : 0 }}
        >
          +{overflow}
        </div>
      ) : null}
    </div>
  );

  if (!showTooltip) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}

const sizeToLoaderSize: Record<AvatarSize, number> = {
  "3xs": 7,
  "2xs": 8,
  xs: 10,
  sm: 12,
  md: 14,
  lg: 16,
  xl: 20,
};
